// vg-binding: P7.D-107
// vg-binding: P7.D-108
// vg-binding: P7.D-134
// vg-binding: P7.D-154
//
// apps/vendor/src/pages/Support.tsx
//
// dbg-support-polish — vendor Support LIST page rebuilt to the admin (V2)
// layout: header + stat cards + tab strip with search + full-width tables.
// Row click navigates to the 2-column detail page (SupportDetail.tsx):
//   /support/tickets/:id   — vendor-raised ticket (mine)
//   /support/assigned/:id  — ticket pushed from admin (assigned)
// The create-ticket modal is unchanged (vendor-raised surface).

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@printway/ui';

import { useTranslation } from '../lib/i18n/index.js';
import {
  displayStatus,
  useAssignedList,
  useCreateTicket,
  useTicketsList,
  type TicketCategory,
  type TicketDisplayStatus,
  type TicketPriority,
} from '../features/support/api.js';

// 'General' KHÔNG có trong create form — nó chỉ là bucket RENDER cho ticket
// folded từ legacy `other` (labels vẫn có key General để render đúng).
const CREATE_CATEGORIES: TicketCategory[] = ['Order', 'Billing', 'Product', 'Account'];
const CREATE_PRIORITIES: TicketPriority[] = ['Low', 'Medium', 'High', 'Critical'];

const PRIORITY_PILL: Record<TicketPriority, string> = {
  Critical: 'bg-red-100 text-red-800',
  High: 'bg-orange-100 text-orange-800',
  Medium: 'bg-pw-surface text-pw-text-muted',
  Low: 'bg-pw-surface text-pw-text',
};
const STATUS_PILL: Record<TicketDisplayStatus, string> = {
  Open: 'bg-blue-100 text-blue-800',
  InProgress: 'bg-yellow-100 text-yellow-800',
  Resolved: 'bg-green-100 text-green-800',
  Closed: 'bg-pw-surface text-pw-text',
  Reopened: 'bg-purple-100 text-purple-800',
};
function fmtMs(ms: number | null | undefined): string {
  if (!ms) return '—';
  return new Date(ms).toLocaleString(undefined, {
    dateStyle: 'short',
    timeStyle: 'short',
  });
}

export function SupportPage(): JSX.Element {
  const { t } = useTranslation('support');
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);

  const mineQ = useTicketsList({ page: 1, pageSize: 100 });
  // The in-page "assigned" table is gone (the tab routes to /dispatches, see
  // I4 below) — the query stays because the stat cards + tab count read it.
  const assignedQ = useAssignedList();

  const mineRows = useMemo(() => {
    const rows = mineQ.data?.rows ?? [];
    if (!search.trim()) return rows;
    const q = search.trim().toLowerCase();
    return rows.filter((r) => r.subject.toLowerCase().includes(q));
  }, [mineQ.data, search]);

  const stats = useMemo(() => {
    const mine = mineQ.data?.rows ?? [];
    const assigned = assignedQ.data?.rows ?? [];
    return {
      mineOpen: mine.filter(
        (r) => displayStatus(r.status) === 'Open' || displayStatus(r.status) === 'InProgress',
      ).length,
      assignedPending: assigned.filter((r) => r.vendor_status === 'Pending').length,
      assignedInProgress: assigned.filter((r) => r.vendor_status === 'InProgress').length,
      assignedDone: assigned.filter((r) => r.vendor_status === 'Done').length,
    };
  }, [mineQ.data, assignedQ.data]);

  return (
    <main
      data-testid="support-page-root"
      className="flex h-full flex-col gap-4 px-6 py-6"
    >
      {/* Header */}
      <PageHeader
        title={t('page.title')}
        subtitle={t('page.subtitle')}
        actions={
          <button
            type="button"
            data-testid="support-add-ticket-btn"
            onClick={() => setShowForm(true)}
            className="rounded-md bg-pw-primary px-4 py-2 text-sm font-semibold text-white hover:bg-pw-primary-strong"
          >
            {t('page.addBtn')}
          </button>
        }
      />

      {/* Stat cards (admin-parity) */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {(
          [
            { key: 'mineOpen', tile: 'bg-indigo-50 text-indigo-600', value: stats.mineOpen, glyph: '🎫' },
            { key: 'assignedPending', tile: 'bg-amber-50 text-amber-600', value: stats.assignedPending, glyph: '⏳' },
            { key: 'assignedInProgress', tile: 'bg-blue-50 text-blue-600', value: stats.assignedInProgress, glyph: '🔧' },
            { key: 'assignedDone', tile: 'bg-emerald-50 text-emerald-600', value: stats.assignedDone, glyph: '✅' },
          ] as const
        ).map((card) => (
          <div
            key={card.key}
            data-testid={`support-stat-${card.key}`}
            className="flex items-center gap-4 rounded-xl border border-pw-border bg-pw-bg p-4"
          >
            <span
              className={`inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-lg ${card.tile}`}
              aria-hidden="true"
            >
              {card.glyph}
            </span>
            <div>
              <div className="text-2xl font-semibold leading-tight text-pw-text">
                {card.value}
              </div>
              <div className="text-sm text-pw-text-muted">
                {t(`stats.${card.key}`)}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Tabs + search */}
      <div className="flex flex-wrap items-center gap-4 border-b border-pw-border">
        {(
          [
            { key: 'mine', testId: 'support-tab-mine', count: (mineQ.data?.rows ?? []).length },
            { key: 'assigned', testId: 'support-tab-assigned', count: (assignedQ.data?.rows ?? []).length },
          ] as const
        ).map((tabDef) => (
          <button
            key={tabDef.key}
            type="button"
            data-testid={tabDef.testId}
            // I4 — the "assigned" surface is now the dispatch queue; route
            // there instead of the legacy in-page assigned table so the
            // /dispatches page is reachable. "mine" is therefore always the
            // active in-page tab (dead assigned branch removed,
            // fix/vendor-batch-e).
            onClick={() => {
              if (tabDef.key === 'assigned') navigate('/dispatches');
            }}
            className={`inline-flex items-center gap-1.5 border-b-2 px-1 pb-2 text-sm font-medium transition-colors ${
              tabDef.key === 'mine'
                ? 'border-pw-primary text-pw-primary'
                : 'border-transparent text-pw-text-muted hover:text-pw-text'
            }`}
          >
            {t(`page.tab.${tabDef.key}`)}
            <span className="rounded-full bg-pw-surface px-1.5 py-0.5 text-xs">
              {tabDef.count}
            </span>
          </button>
        ))}
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('listV2.searchPlaceholder')}
          data-testid="support-search"
          className="mb-2 ml-auto w-64 rounded-md border border-pw-border bg-pw-bg px-3 py-1.5 text-sm focus:outline-none"
        />
      </div>

      {/* Table — vendor-raised tickets ("assigned" lives at /dispatches). */}
      <div className="overflow-hidden rounded-xl border border-pw-border">
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-pw-surface text-left text-xs font-medium uppercase text-pw-text-muted">
              <tr>
                <th className="px-4 py-2.5">{t('listV2.colSubject')}</th>
                <th className="px-4 py-2.5">{t('listV2.colCategory')}</th>
                <th className="px-4 py-2.5">{t('listV2.colPriority')}</th>
                <th className="px-4 py-2.5">{t('listV2.colStatus')}</th>
                <th className="px-4 py-2.5">{t('listV2.colLastResponse')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-pw-border">
              {mineQ.isLoading ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-pw-text-muted">
                    {t('listV2.loading')}
                  </td>
                </tr>
              ) : mineRows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-pw-text-muted">
                    {t('list.empty')}
                  </td>
                </tr>
              ) : (
                mineRows.map((row) => (
                  <tr
                    key={row._id}
                    data-testid={`support-ticket-row-${row._id}`}
                    onClick={() => navigate(`/support/tickets/${row._id}`)}
                    className="cursor-pointer hover:bg-pw-surface"
                  >
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-2">
                        <span className="font-medium text-pw-text">{row.subject}</span>
                      </span>
                    </td>
                    <td className="px-4 py-3 text-pw-text-muted">
                      {t(`categoryLabels.${row.category}`)}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${PRIORITY_PILL[row.priority]}`}>
                        {t(`priorityLabels.${row.priority}`)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS_PILL[displayStatus(row.status)]}`}>
                        {t(`statusLabels.${displayStatus(row.status)}`)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-pw-text-muted">
                      {fmtMs(row.lastMessageAt)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          </div>
        </div>

      {showForm ? (
        <CreateTicketModal
          onClose={() => setShowForm(false)}
          onCreated={(id) => {
            setShowForm(false);
            navigate(`/support/tickets/${id}`);
          }}
        />
      ) : null}
    </main>
  );
}

// ---------------------------------------------------------------------------
// Create-ticket modal — unchanged surface (vendor-raised tickets).
// ---------------------------------------------------------------------------

function CreateTicketModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
}): JSX.Element {
  const { t } = useTranslation('support');
  const createMut = useCreateTicket();
  const [form, setForm] = useState({
    category: 'Order' as TicketCategory,
    priority: 'Medium' as TicketPriority,
    subject: '',
    description: '',
  });

  async function submitCreate() {
    if (!form.subject.trim() || !form.description.trim()) return;
    try {
      const created = await createMut.mutateAsync({
        subject: form.subject,
        description: form.description,
        category: form.category,
        priority: form.priority,
      });
      onCreated(created._id);
    } catch {
      // error surfaced via mut.error
    }
  }

  return (
    <div
      data-testid="support-ticket-form-modal"
      /* dark-ok: modal scrim, translucent black reads correctly in both modes */
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-md rounded-xl bg-pw-bg p-6 shadow-xl">
        <h2 className="mb-4 text-lg font-semibold text-pw-text">{t('form.title')}</h2>
        <div className="flex flex-col gap-3">
          <label className="text-sm font-medium text-pw-text">
            {t('form.category')}
            <select
              data-testid="support-ticket-category"
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value as TicketCategory })}
              className="mt-1 w-full rounded-md border border-pw-border bg-pw-bg px-3 py-2 text-sm"
            >
              {CREATE_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {t(`categoryLabels.${c}`)}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm font-medium text-pw-text">
            {t('form.priority')}
            <select
              data-testid="support-ticket-priority"
              value={form.priority}
              onChange={(e) => setForm({ ...form, priority: e.target.value as TicketPriority })}
              className="mt-1 w-full rounded-md border border-pw-border bg-pw-bg px-3 py-2 text-sm"
            >
              {CREATE_PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {t(`priorityLabels.${p}`)}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm font-medium text-pw-text">
            {t('form.subject')}
            <input
              data-testid="support-ticket-subject"
              type="text"
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              className="mt-1 w-full rounded-md border border-pw-border bg-pw-bg px-3 py-2 text-sm"
              maxLength={200}
            />
          </label>
          <label className="text-sm font-medium text-pw-text">
            {t('form.body')}
            <textarea
              data-testid="support-ticket-body"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={4}
              className="mt-1 w-full rounded-md border border-pw-border bg-pw-bg px-3 py-2 text-sm"
              maxLength={5000}
            />
          </label>
          {createMut.error ? (
            <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
              {createMut.error.message}
            </p>
          ) : null}
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-pw-border bg-pw-bg px-4 py-2 text-sm font-medium text-pw-text"
          >
            {t('form.cancel')}
          </button>
          <button
            type="button"
            data-testid="support-ticket-submit"
            disabled={!form.subject.trim() || !form.description.trim() || createMut.isPending}
            onClick={() => void submitCreate()}
            className="rounded-md bg-pw-primary px-4 py-2 text-sm font-semibold text-white hover:bg-pw-primary-strong disabled:opacity-60"
          >
            {createMut.isPending ? t('form.submitting') : t('form.submit')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default SupportPage;
